import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const PRIMARY_COLOR = '#3498db';
const PRIMARY_DARK = '#2980b9';
const ERROR_COLOR = '#e74c3c';

const GetBilty: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [biltyLink, setBiltyLink] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid bilty link.');
      setLoading(false);
      return;
    }

    const fetchBilty = async () => {
      try {
        const base = (process.env.REACT_APP_BACKEND_API_ROUTE || '').replace(/\/$/, '');
        const res = await fetch(`${base}/orders/bilty/${encodeURIComponent(token)}`);

        if (res.status === 404) {
          setError('Bilty not found. The link may be invalid or has expired.');
          setLoading(false);
          return;
        }

        if (!res.ok) {
          throw new Error(`Server error (${res.status})`);
        }

        const data = await res.json();

        if (!data.biltyLink) {
          setError('Bilty PDF is not available yet for this shipment.');
          setLoading(false);
          return;
        }

        setBiltyLink(data.biltyLink);
        setLoading(false);

        // Redirect current tab to bilty PDF
        window.location.href = data.biltyLink;
      } catch (err: any) {
        setError(err.message || 'Failed to load bilty. Please try again.');
        setLoading(false);
      }
    };

    fetchBilty();
  }, [token]);

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f4f8',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    padding: '24px 16px',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '40px 32px',
    maxWidth: '480px',
    width: '100%',
    boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
    textAlign: 'center',
  };

  const logoStyle: React.CSSProperties = {
    fontSize: '28px',
    fontWeight: 700,
    color: PRIMARY_COLOR,
    marginBottom: '8px',
    letterSpacing: '-0.5px',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '14px',
    color: '#666',
    marginBottom: '32px',
  };

  const iconStyle: React.CSSProperties = {
    fontSize: '64px',
    marginBottom: '16px',
  };

  const headingStyle: React.CSSProperties = {
    fontSize: '22px',
    fontWeight: 600,
    color: '#2c3e50',
    marginBottom: '8px',
  };

  const bodyTextStyle: React.CSSProperties = {
    fontSize: '15px',
    color: '#555',
    marginBottom: '28px',
    lineHeight: 1.6,
  };

  const btnStyle: React.CSSProperties = {
    display: 'inline-block',
    backgroundColor: PRIMARY_COLOR,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 28px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'background-color 0.2s',
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={logoStyle}>Instaship</div>
          <div style={subtitleStyle}>Logistics Made Simple</div>
          <div style={{ ...iconStyle, animation: 'spin 1s linear infinite' }}>⏳</div>
          <div style={headingStyle}>Loading your Bilty…</div>
          <div style={bodyTextStyle}>Please wait while we retrieve your document.</div>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={logoStyle}>Instaship</div>
          <div style={subtitleStyle}>Logistics Made Simple</div>
          <div style={iconStyle}>❌</div>
          <div style={{ ...headingStyle, color: ERROR_COLOR }}>Bilty Not Found</div>
          <div style={bodyTextStyle}>{error}</div>
          <div style={{ fontSize: '13px', color: '#999' }}>
            If you believe this is a mistake, please contact Instaship support.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={logoStyle}>Instaship</div>
        <div style={subtitleStyle}>Logistics Made Simple</div>
        <div style={iconStyle}>📄</div>
        <div style={headingStyle}>Your Bilty is Ready</div>
        <div style={bodyTextStyle}>
          Your bilty (LR copy) has opened in a new tab.{'\n'}
          If it didn't open automatically, click the button below.
        </div>
        <a
          href={biltyLink}
          style={btnStyle}
          onMouseEnter={e => ((e.target as HTMLAnchorElement).style.backgroundColor = PRIMARY_DARK)}
          onMouseLeave={e => ((e.target as HTMLAnchorElement).style.backgroundColor = PRIMARY_COLOR)}
        >
          Open Bilty PDF
        </a>
      </div>
    </div>
  );
};

export default GetBilty;
